import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { ArchiveProjectDialog } from '@/features/projects/components/ArchiveProjectDialog';
import { useWorkspaceRole } from '@/lib/hooks/useWorkspaceRole';
import { getProjectBySlugQuery } from '@/lib/supabase/queries/projects';
import { listStatesByProjectQuery } from '@/lib/supabase/queries/states';
import { getWorkspaceBySlugQuery } from '@/lib/supabase/queries/workspaces';
import { cn } from '@/lib/utils';
import { useQuery } from '@tanstack/react-query';
import {
  Link,
  Outlet,
  createFileRoute,
  notFound,
  useParams,
  useRouterState,
} from '@tanstack/react-router';
import {
  Archive,
  BarChart3,
  CheckCircle2,
  GanttChart,
  KanbanSquare,
  ListChecks,
  Repeat,
  Settings,
  Sparkles,
  Target,
} from 'lucide-react';
import { useState } from 'react';

export const Route = createFileRoute('/_app/$wsSlug/projects/$projectSlug')({
  // Precarga la cascada inherente ws -> proj -> states en el cache de React
  // Query usando los MISMOS queryKeys que los componentes (getWorkspaceBySlugQuery,
  // getProjectBySlugQuery, listStatesByProjectQuery). Asi board.tsx y KanbanView
  // hacen cache-hit al montar y arrancan work_items/unread de inmediato, sin la
  // cascada secuencial post-mount. NO precarga work_items (acopla con filtros del
  // store) ni unread (es no-bloqueante por diseno, FIX D4').
  loader: async ({ context, params }) => {
    const { queryClient } = context;
    const { wsSlug, projectSlug } = params;

    const ws = await queryClient.ensureQueryData(getWorkspaceBySlugQuery(wsSlug));
    if (!ws) throw notFound();

    const proj = await queryClient.ensureQueryData(getProjectBySlugQuery(ws.id, projectSlug));
    if (!proj) throw notFound();

    await queryClient.ensureQueryData(listStatesByProjectQuery(proj.id));

    // Los componentes leen del cache de RQ via sus useQuery existentes; no usan loaderData.
    return null;
  },
  component: ProjectSlugLayout,
  pendingComponent: ProjectLayoutPending,
  // Si el loader resuelve antes de 150ms (cache-hit en navegacion interna o
  // preload previo), NO se muestra el pending -> evita el flash.
  pendingMs: 150,
  // Cuando se muestra, dura minimo 300ms -> evita el parpadeo.
  pendingMinMs: 300,
  notFoundComponent: () => (
    <div className="p-6 text-sm text-quiet">Proyecto no encontrado o sin permisos para verlo.</div>
  ),
  errorComponent: () => (
    <div className="p-6 text-sm text-quiet">
      Ocurrió un error cargando el proyecto. Intenta recargar la página.
    </div>
  ),
});

/**
 * Pending liviano del layout de proyecto. Discreto a proposito: lo comparten
 * board, list, gantt, cycles, etc. — NO es el skeleton de 4 columnas del board
 * (ese vive dentro de KanbanView para el loading de work_items). Reproduce el
 * chrome del header (titulo + tabs) para que la transicion no salte.
 */
function ProjectLayoutPending() {
  return (
    <div className="flex flex-col h-full min-h-0">
      <header className="px-6 pt-4 pb-0 border-b border-border bg-surface flex-shrink-0">
        <div className="flex items-center gap-3 mb-3">
          <Skeleton className="h-6 w-48" />
        </div>
        <div className="flex gap-2 pb-2">
          {['t1', 't2', 't3', 't4'].map((t) => (
            <Skeleton key={t} className="h-7 w-20" />
          ))}
        </div>
      </header>
      <div className="flex-1 min-h-0 p-6">
        <Skeleton className="h-4 w-64 mb-3" />
        <Skeleton className="h-4 w-40" />
      </div>
    </div>
  );
}

const TABS = [
  { to: 'board', label: 'Tablero', icon: KanbanSquare, managerOnly: false },
  { to: 'list', label: 'Lista', icon: ListChecks, managerOnly: false },
  { to: 'gantt', label: 'Gantt', icon: GanttChart, managerOnly: false },
  { to: 'cycles', label: 'Ciclos', icon: Sparkles, managerOnly: false },
  { to: 'modules', label: 'Módulos', icon: BarChart3, managerOnly: false },
  { to: 'recurrentes', label: 'Recurrentes', icon: Repeat, managerOnly: false },
  { to: 'kpis', label: 'KPIs', icon: Target, managerOnly: false },
  { to: 'settings', label: 'Configuración', icon: Settings, managerOnly: true },
  { to: 'archivadas', label: 'Archivadas', icon: Archive, managerOnly: false },
] as const;

const TODO_HIDDEN_TABS = new Set(['gantt', 'cycles', 'modules', 'kpis']);

/**
 * Layout for any /projects/:projectSlug/* route. Renders a sub-nav with the
 * project's main views and an outlet for the active sub-route.
 *
 * The work-item detail route (`/projects/:projectSlug/:itemId`) sits under
 * this layout but doesn't match any tab — none will appear active there,
 * which is intentional (the detail is a focused view; tabs return the user
 * to the list/board they came from).
 */
function ProjectSlugLayout() {
  const { wsSlug, projectSlug } = useParams({ strict: false }) as {
    wsSlug: string;
    projectSlug: string;
  };
  const pathname = useRouterState({ select: (s) => s.location.pathname });

  const { data: ws } = useQuery(getWorkspaceBySlugQuery(wsSlug));
  const { data: project } = useQuery({
    ...getProjectBySlugQuery(ws?.id ?? '', projectSlug),
    enabled: !!ws?.id,
  });

  const { canManage } = useWorkspaceRole(wsSlug);
  const isTodo = project?.kind === 'todo';
  const visibleTabs = TABS.filter(
    (t) => (!t.managerOnly || canManage) && !(isTodo && TODO_HIDDEN_TABS.has(t.to)),
  );

  const [archiveOpen, setArchiveOpen] = useState(false);

  return (
    <div className="flex flex-col h-full min-h-0">
      <header className="px-6 pt-4 pb-0 border-b border-border bg-surface flex-shrink-0">
        <div className="flex items-center gap-3 mb-3">
          {project?.icon && <span className="text-xl leading-none">{project.icon}</span>}
          <h1 className="text-lg font-semibold text-ink truncate">{project?.name ?? 'Proyecto'}</h1>
          {project?.identifier && (
            <span className="identifier text-xs text-quiet">{project.identifier}</span>
          )}
          {canManage && project && ws && (
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={() => setArchiveOpen(true)}
              className="ml-auto h-7 gap-1.5 text-xs"
              title="Marca el proyecto como finalizado y lo saca de la lista activa. Reversible."
            >
              <CheckCircle2 size={13} />
              Finalizar proyecto
            </Button>
          )}
        </div>
        {canManage && project && ws && (
          <ArchiveProjectDialog
            open={archiveOpen}
            onOpenChange={setArchiveOpen}
            projectId={project.id}
            projectName={project.name}
            workspaceId={ws.id}
            workspaceSlug={wsSlug}
          />
        )}

        <nav className="flex gap-0 overflow-x-auto" aria-label="Vistas del proyecto">
          {visibleTabs.map(({ to, label, icon: Icon }) => {
            const fullPath = `/${wsSlug}/projects/${projectSlug}/${to}`;
            const isActive = pathname === fullPath || pathname.startsWith(`${fullPath}/`);
            return (
              <Link
                key={to}
                to={`/$wsSlug/projects/$projectSlug/${to}` as never}
                params={{ wsSlug, projectSlug } as never}
                className={cn(
                  'inline-flex items-center gap-1.5 px-3 h-9 text-sm font-medium border-b-2 transition-colors shrink-0',
                  isActive
                    ? 'border-accent text-accent'
                    : 'border-transparent text-quiet hover:text-body hover:border-border',
                )}
                aria-current={isActive ? 'page' : undefined}
              >
                <Icon size={14} />
                {label}
              </Link>
            );
          })}
        </nav>
      </header>

      <div className="flex-1 min-h-0">
        <Outlet />
      </div>
    </div>
  );
}
